/*---------------------------------------------------------------
 Project: Ring2All Platform
 Author: Rodrigo Cuadra
 Company: Ring2All LLC
 File: ClassOfServiceFormView.tsx
 Created: 2025-01-27
 Description:
    Form view for creating and editing Class of Services.

Notes:
    - Refactored from monolithic 612-line file
    - Uses hooks for state management
    - Maintains QuickSearchModal functionality

Last Updated: 2025-01-27 by Rodrigo Cuadra (Refactored)
----------------------------------------------------------------*/

import { useState, useEffect, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Shield, Search, Copy, Plus } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import Toggle from '../../components/ui/Toggle';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import FeatureCategorySelect from '../../components/ui/FeatureCategorySelect';
import DialRestrictionSelect from '../../components/ui/DialRestrictionSelect';
import SelectARS from '../../components/ui/SelectARS';
import SelectModule from '../../components/ui/SelectModule';
import SelectDestination from '../../components/ui/SelectDestination';
import { ModuleFormHeader } from '../../components/layouts';
import FixedActionBar from '../../components/ui/FixedActionBar';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { useClassOfServiceData } from './hooks/useClassOfServiceData';
import { useClassOfServiceForm } from './hooks/useClassOfServiceForm';
import { deleteClassOfService } from './api/classOfServicesApi';
import { fetchClassOfServicesForQuickList } from './api/classOfServicesApi';
import { navigateToClassOfServiceDuplicate } from './classOfServicesActions';
import { ClassOfService } from './types';

type FormFieldProps = {
  id: string;
  label: string;
  tooltip: string;
  children: ReactNode;
};

const FormField = ({ id, label, tooltip, children }: FormFieldProps) => (
  <>
    <SimpleTooltip content={tooltip}>
      <Label htmlFor={id} className="text-left">
        {label}
      </Label>
    </SimpleTooltip>
    {children}
  </>
);

export function ClassOfServiceFormView() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');
  const navigate = useNavigate();
  const { t } = useI18n();
  const { notifications, removeNotification, showSuccess, showError } = useNotifications();

  const mode = id === 'new' ? 'create' : 'edit';
  const isEditMode = mode === 'edit';

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [quickListProfiles, setQuickListProfiles] = useState<ClassOfService[]>([]);
  const [isLoadingQuickList, setIsLoadingQuickList] = useState(false);

  const {
    isLoading,
    formData: loadedFormData,
    featureCodeCategories,
    dialRuleRestrictions,
    arsProfiles,
    isLoadingOptions,
  } = useClassOfServiceData(id, duplicateId);

  const {
    formData,
    errors,
    isSaving,
    hasChanges,
    handleChange,
    handleSubmit,
    handleCancel,
  } = useClassOfServiceForm(loadedFormData, id, isEditMode);

  // Enhanced save handler for useSaveWithAction
  const handleSaveInternal = useCallback(async () => {
    const savedData = await handleSubmit();
    // If no changes were detected, return current formData to allow navigation
    if (!savedData) {
      return formData;
    }
    return savedData;
  }, [handleSubmit, formData]);

  // Use reusable hook for save actions
  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/pbx/class-of-service/class',
    newPath: '/pbx/class-of-service/class/new',
    mode: isEditMode ? 'edit' : 'create',
    getEditPath: (savedData) => `/pbx/class-of-service/class/${savedData?.uuid || savedData?.id || id}`,
    getFormData: () => formData,
    storageKey: 'classOfServices:saveAndNewData',
  });

  const loadQuickList = useCallback(async () => {
    setIsLoadingQuickList(true);
    try {
      const profiles = await fetchClassOfServicesForQuickList(t);
      setQuickListProfiles(profiles);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : t('classOfServices.messages.quickListError');
      showError(message);
      setQuickListProfiles([]);
    } finally {
      setIsLoadingQuickList(false);
    }
  }, [showError, t]);

  useEffect(() => {
    if (showQuickList) {
      loadQuickList();
    }
  }, [loadQuickList, showQuickList]);

  const handleDeleteClick = useCallback(() => {
    if (isEditMode && id) {
      setShowDeleteModal(true);
    }
  }, [id, isEditMode]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!isEditMode || !id) {
      return;
    }

    setIsDeleting(true);
    try {
      await deleteClassOfService(id, t);

      showSuccess(t('classOfServices.messages.deleteSuccess'));
      navigate('/pbx/class-of-service/class');
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : t('classOfServices.messages.deleteError');
      showError(message);
      setShowDeleteModal(false);
    } finally {
      setIsDeleting(false);
    }
  }, [id, isEditMode, navigate, showError, showSuccess, t]);

  const handleDuplicateFromForm = useCallback(() => {
    if (!isEditMode || !id) return;
    navigateToClassOfServiceDuplicate(navigate, id);
  }, [isEditMode, id, navigate]);

  // Navigate to new
  const handleAddNew = useCallback(() => {
    navigate('/pbx/class-of-service/class/new');
  }, [navigate]);

  const handleQuickListSelect = useCallback(
    (profile: ClassOfService) => {
      handleChange('featureCodeCosId', profile.featureCodeCosId ?? null);
      handleChange('dialRuleRestrictionId', profile.dialRuleRestrictionId ?? null);
      handleChange('arsId', profile.arsId ?? null);
      handleChange('isMain', profile.isMain ?? false);
      handleChange('enabled', profile.enabled ?? true);
      setShowQuickList(false);
      setQuickListSearch('');
    },
    [handleChange]
  );

  const filteredQuickList = useMemo(() => {
    const query = quickListSearch.trim().toLowerCase();
    if (!query) {
      return quickListProfiles;
    }

    return quickListProfiles.filter(
      (profile) =>
        profile.name?.toLowerCase().includes(query) ||
        profile.description?.toLowerCase().includes(query)
    );
  }, [quickListProfiles, quickListSearch]);

  if (isLoading || !formData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="p-6"><ModuleFormHeader
        icon={<Shield className="h-5 w-5 text-primary" />}
        title={
          isEditMode
            ? `${t('classOfServices.editClassOfService')} - ${formData.name || ''}`
            : t('classOfServices.createClassOfService')
        }
        backButtonText={t('classOfServices.actions.list')}
        onBack={() => navigate('/pbx/class-of-service/class')}
        actions={[
          // 1. Quick Search (always visible)
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('classOfServices.actions.quickList'),
            onClick: () => setShowQuickList(true),
          },
          // 2. Duplicate (edit mode only)
          ...(isEditMode
            ? [
              {
                icon: <Copy className="h-5 w-5 text-muted-foreground" />,
                tooltip: t('classOfServices.actions.duplicate'),
                onClick: handleDuplicateFromForm,
              },
            ]
            : []),
          // 3. Add New (always visible)
          {
            icon: <Plus className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('classOfServices.actions.add'),
            onClick: handleAddNew,
          }
        ]}
      />

      <div className="mt-8">
        <form className="bg-card p-6 rounded-lg border border-border space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
            <FormField
              id="name"
              label={t('classOfServices.fields.name.label')}
              tooltip={t('classOfServices.fields.name.tooltip')}
            >
              <div>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(event) => handleChange('name', event.target.value)}
                  placeholder={t('classOfServices.fields.name.placeholder')}
                  className={errors.name ? 'border-red-500' : ''}
                  disabled={isEditMode && loadedFormData?.isMain}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-500">{errors.name}</p>
                )}
              </div>
            </FormField>

            <FormField
              id="description"
              label={t('classOfServices.fields.description.label')}
              tooltip={t('classOfServices.fields.description.tooltip')}
            >
              <Input
                id="description"
                value={formData.description}
                onChange={(event) => handleChange('description', event.target.value)}
                placeholder={t('classOfServices.fields.description.placeholder')}
              />
            </FormField>

            <FormField
              id="featureCodeCosId"
              label={t('classOfServices.fields.featureCodeCosId.label')}
              tooltip={t('classOfServices.fields.featureCodeCosId.tooltip')}
            >
              <FeatureCategorySelect
                value={formData.featureCodeCosId}
                onChange={(value) => handleChange('featureCodeCosId', value)}
                options={featureCodeCategories}
                placeholder={t('classOfServices.fields.featureCodeCosId.placeholder')}
                disabled={isLoadingOptions}
              />
            </FormField>

            <FormField
              id="dialRuleRestrictionId"
              label={t('classOfServices.fields.dialRuleRestrictionId.label')}
              tooltip={t('classOfServices.fields.dialRuleRestrictionId.tooltip')}
            >
              <DialRestrictionSelect
                value={formData.dialRuleRestrictionId}
                onChange={(value) => handleChange('dialRuleRestrictionId', value)}
                options={dialRuleRestrictions}
                placeholder={t('classOfServices.fields.dialRuleRestrictionId.placeholder')}
                disabled={isLoadingOptions}
              />
            </FormField>

            <FormField
              id="arsId"
              label={t('classOfServices.fields.arsId.label')}
              tooltip={t('classOfServices.fields.arsId.tooltip')}
            >
              <SelectARS
                value={formData.arsId}
                onChange={(value) => handleChange('arsId', value)}
                profiles={arsProfiles}
                placeholder={t('classOfServices.fields.arsId.placeholder')}
                disabled={isLoadingOptions}
              />
            </FormField>

            <FormField
              id="isMain"
              label={t('classOfServices.fields.isMain.label')}
              tooltip={t('classOfServices.fields.isMain.tooltip')}
            >
              <Toggle
                id="isMain"
                value={formData.isMain}
                onChange={(value) => handleChange('isMain', value)}
              />
            </FormField>

            <FormField
              id="failoverDestinationModule"
              label={t('classOfServices.fields.failoverDestinationModule.label')}
              tooltip={t('classOfServices.fields.failoverDestinationModule.tooltip')}
            >
              <SelectModule
                value={formData.failoverDestinationModule || 'terminate'}
                onChange={(value) => handleChange('failoverDestinationModule', value)}
              />
            </FormField>

            <FormField
              id="failoverDestinationData"
              label={t('classOfServices.fields.failoverDestinationData.label')}
              tooltip={t('classOfServices.fields.failoverDestinationData.tooltip')}
            >
              <SelectDestination
                moduleType={formData.failoverDestinationModule || 'terminate'}
                value={formData.failoverDestinationData || ''}
                onChange={(value) => handleChange('failoverDestinationData', value)}
              />
            </FormField>

            <FormField
              id="enabled"
              label={t('classOfServices.fields.enabled.label')}
              tooltip={t('classOfServices.fields.enabled.tooltip')}
            >
              <div className="flex flex-col gap-1">
                <Toggle
                  id="enabled"
                  value={formData.enabled}
                  onChange={(value) => handleChange('enabled', value)}
                  disabled={formData.isMain}
                />
                {formData.isMain && (
                  <span className="text-xs text-muted-foreground italic">
                    {t('classOfServices.messages.defaultMustBeEnabled')}
                  </span>
                )}
              </div>
            </FormField>
          </div>
        </form>
      </div>

      <FixedActionBar
        onSaveWithAction={handleSaveWithAction}
        onCancel={handleCancel}
        onDelete={isEditMode && !loadedFormData?.isMain ? handleDeleteClick : undefined}
        isSaving={isSaving}
        isEditMode={isEditMode}
        useSaveDropdown={true}
        saveText={t('classOfServices.actions.save')}
        cancelText={t('classOfServices.actions.cancel')}
        deleteText={t('classOfServices.actions.delete')}
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title={t('classOfServices.messages.confirmDeleteTitle')}
        message={t('classOfServices.messages.confirmDeleteMessage', {
          name: formData.name || t('classOfServices.className'),
        })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />

      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
        }}
        title={t('classOfServices.quickList.title')}
        items={filteredQuickList}
        isLoadingInitial={isLoadingQuickList && quickListProfiles.length === 0}
        isSearching={false}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(profile) => handleQuickListSelect(profile)}
        renderItem={(profile) => (
          <button
            type="button"
            onClick={() => handleQuickListSelect(profile)}
            className="w-full rounded-lg border border-border p-3 text-left transition-colors hover:border-primary hover:bg-accent"
          >
            <div className="font-medium text-foreground">{profile.name}</div>
            {profile.description && (
              <div className="mt-1 text-sm text-muted-foreground">{profile.description}</div>
            )}
          </button>
        )}
        searchPlaceholder={t('classOfServices.quickList.searchPlaceholder')}
        emptyMessage={t('classOfServices.quickListNoResults')}
        headerIcon={<Shield className="h-5 w-5" />}
        minSearchChars={0}
      />
    </div>
  );
}

export default ClassOfServiceFormView;
